import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { ComponentType, CSSProperties, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { APPS, CHANNELS } from './onboardingOptions';
import { useGettingStartedOnboarding } from './useGettingStartedOnboarding';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import type { SaasRemedy } from '@/Domain/helper/saasHostRouter';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

type Glyph = ComponentType<{ className?: string; style?: CSSProperties }>;

/**
 * Shown when onboarding cannot scrape a host we can nonetheless reach another way
 * (`classifySaasHost`). It deliberately does NOT navigate.
 *
 * The obvious move — a "Connect Discord" button pointing at `/channels?open=discord` — cannot
 * work here: `DefaultLayout`'s route guard sends every product route back to Getting Started
 * until onboarding completes, and `pickOAuthCallbackSearch` drops `?open=` on the way, so the
 * user lands back on the screen they just tried to leave. Beyond the broken param, a user who
 * escaped mid-onboarding would arrive at Actions with no knowledge base and no identity: a second
 * dead end.
 *
 * So the intent is deferred instead. Clicking writes the destination into the same onboarding
 * selection the Deployment / External apps steps write to, which `PostOnboardingChecklist`
 * already turns into a working `?open=` deep link on the AI Preview page the user lands on the
 * moment onboarding finishes. No new plumbing on the destination side.
 */
export const SaasRemedyHint = ({
  remedy,
  urlHost,
  source,
}: {
  remedy: SaasRemedy;
  urlHost?: string;
  /** Which surface rendered the hint, for the funnel: the pre-submit notice or the error view. */
  source: 'known_bad_url' | 'scrape_error';
}) => {
  const { t } = useTranslation('getting_started');
  const { capture } = usePostHogAnalytics();
  const { externalAppsSelection, deploymentSelection, rememberSelection } =
    useGettingStartedOnboarding();

  const target = remedy.kind === 'channel' ? 'deployment' : 'external_apps';
  const selection = target === 'deployment' ? deploymentSelection : externalAppsSelection;
  const added = selection.has(remedy.openId);

  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    capture(PostHogEventEnum.GettingStartedSaasRemedyShown, {
      source,
      kind: remedy.kind,
      open_id: remedy.openId,
      url_host: urlHost,
      already_selected: added,
    });
    // `added` is read at first paint only: re-firing the impression after the user's own click
    // would double-count the funnel's top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Icon = glyphFor(remedy.openId);

  const handleAdd = () => {
    // Self-hosted Jira / Confluence share the generic `other_api` id, which would otherwise reach
    // the checklist as an unlabelled "Other API" row — pass the product name along with it.
    const otherText = remedy.kind === 'api' ? remedy.name : undefined;
    rememberSelection(target, remedy.openId, otherText);
    capture(PostHogEventEnum.GettingStartedSaasRemedyAdded, {
      source,
      kind: remedy.kind,
      open_id: remedy.openId,
      url_host: urlHost,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="w-full rounded-xl border border-border bg-muted/40 p-4 text-left"
      data-testid="gs-saas-remedy-hint"
      data-remedy-kind={remedy.kind}
      data-remedy-state={added ? 'added' : 'idle'}
    >
      <div className="flex gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-card">
          {added ? (
            <Check className="size-4 text-success" data-testid="gs-saas-remedy-hint-check" />
          ) : (
            <Icon className="size-4" />
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-sm font-medium text-foreground">
            {added
              ? t('saas_remedy.added_title', { name: remedy.name })
              : t(`saas_remedy.${remedy.kind}.title`, { name: remedy.name })}
          </p>
          <p className="text-sm text-muted-foreground">
            {added
              ? t('saas_remedy.added_description', { name: remedy.name })
              : t(`saas_remedy.${remedy.kind}.description`, {
                  name: remedy.name,
                  host: urlHost ?? '',
                })}
          </p>
          {!added && (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAdd}
                data-testid="gs-saas-remedy-hint-add"
              >
                {t('saas_remedy.cta', { name: remedy.name })}
              </Button>
              {remedy.docsUrl && (
                <a
                  href={remedy.docsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  data-testid="gs-saas-remedy-hint-docs"
                >
                  {t('saas_remedy.docs_link', { name: remedy.name })}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Reuse the onboarding pill artwork so the hint, the Deployment/External-apps pills, and the
// post-onboarding checklist row all show the same mark for the same destination.
const glyphFor = (openId: string): Glyph => {
  const option =
    CHANNELS.find((channel) => channel.id === openId) ?? APPS.find((app) => app.id === openId);
  if (!option) return FallbackGlyph;
  const { icon: OptionIcon, iconColor } = option;
  return function RemedyGlyph({ className }) {
    return (
      <OptionIcon className={className} style={iconColor ? { color: iconColor } : undefined} />
    );
  };
};

const FallbackGlyph: Glyph = ({ className, style }) => (
  <Check className={className} style={style} />
);
